// Background images live in this browser's IndexedDB (they are too large for
// localStorage). Everything degrades to "unsupported" when IndexedDB is
// blocked, e.g. in private windows.

const DB_NAME = 'sleekhear-media';
const STORE = 'backgrounds';
export const MAX_BYTES = 10 * 1024 * 1024;
export const MAX_WIDTH = 1920;
export const MAX_HEIGHT = 1080;
const ALLOWED = ['image/png', 'image/jpeg', 'image/webp'];

export const isMediaStoreSupported = () => typeof indexedDB !== 'undefined' && !!indexedDB;

const request = (req) => new Promise((resolve, reject) => {
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

function openDb() {
  return new Promise((resolve, reject) => {
    if (!isMediaStoreSupported()) {
      reject(new Error('Browser storage is not available'));
      return;
    }
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(STORE, { keyPath: 'id' });
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, mode);
    const result = await fn(tx.objectStore(STORE));
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return result;
  } finally {
    db.close();
  }
}

/** Returns an error message, or null when the file is acceptable. */
export function validateImage(file) {
  if (!file) return 'No file selected';
  if (!ALLOWED.includes(file.type)) return 'Use a PNG, JPEG or WebP image';
  if (file.size > MAX_BYTES) return 'Image is larger than 10 MB';
  return null;
}

// Shrink big photos to screen size so they load fast and use little storage.
async function downscale(file) {
  try {
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_WIDTH / bitmap.width, MAX_HEIGHT / bitmap.height);
    if (scale === 1) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.88));
    return blob || file;
  } catch {
    return file;
  }
}

// Lets screens and the sync react when images are added or removed.
const listeners = new Set();
export function onImagesChanged(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
const notify = () => listeners.forEach(fn => { try { fn(); } catch { /* a listener must not break saving */ } });

const newId = () => `bg_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

export async function saveImage(file) {
  const error = validateImage(file);
  if (error) throw new Error(error);
  const blob = await downscale(file);
  const record = { id: newId(), name: file.name || 'Background', blob, createdAt: Date.now() };
  await withStore('readwrite', store => request(store.put(record)));
  notify();
  return { id: record.id, name: record.name, createdAt: record.createdAt };
}

/** Stores an image that already has an id (one downloaded from the cloud). */
export async function putImage({ id, name = 'Background', blob, createdAt = Date.now() }) {
  await withStore('readwrite', store => request(store.put({ id, name, blob, createdAt })));
  notify();
}

export async function clearImages() {
  await withStore('readwrite', store => request(store.clear()));
  notify();
}

export async function listImages() {
  const all = await withStore('readonly', store => request(store.getAll()));
  return all
    .map(({ id, name, createdAt }) => ({ id, name, createdAt }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

export async function getImage(id) {
  if (!id) return null;
  const record = await withStore('readonly', store => request(store.get(id)));
  return record ? record.blob : null;
}

export async function deleteImage(id) {
  await withStore('readwrite', store => request(store.delete(id)));
  notify();
}
